import React, { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router';
import { useInfo } from '../context/useInfo';
import ArticleViewBase from './ArticleView';
import LinkButton from './inputs/LinkButton';
import BaseInput from './inputs/BaseInput';
import BaseTextArea from './inputs/BaseTextArea';
import utils from '../utils';
import { galleryResource } from '../types/galleryResource';
import { EditorButton } from './EditorButton';
import AvailableSpaceIndicator from './AvailableSpaceIndicator';
import LoadingPage from './LoadingPage';
import VideoPlayer from './VideoPlayer';
import VaultFileUpload from './VaultFileUpload';
import { diffChars } from 'diff';

export interface ArticleData {
    path: string,
    title: string,
    content: string,
    tags: { id: number, name: string }[]
}

interface EditorProps {
    title?: string,
    baseArticle?: ArticleData
    onSubmit: (article: ArticleData, publish: boolean) => Promise<Record<string, any>>
    showDraftActions?: boolean,
    cancelURL?: string
    availableTags?: { category_id: string, name: string, description: string }[]
}

function ArticleEditor({
    title = "Create wiki page",
    availableTags = [],
    baseArticle = {
        path: "",
        title: "",
        content: "",
        tags: []
    },
    onSubmit,
    showDraftActions = true,
    cancelURL = "/dashboard"
}: EditorProps) {
    const { info, fetchWeb } = useInfo();
    if (info == null) return <></>

    const navigate = useNavigate();

    const [confirmCancelModal, setConfirmCancelModal] = useState<boolean>(false);

    const [sessionStart, _setSessionStart] = useState(new Date());
    const [articleData, setArticleData] = useState(baseArticle);

    const [showingPreview, setShowingPreview] = useState<boolean>(false);
    const [responseMessage, setResponseMesage] = useState<null | { message: string, path: string, error?: string }>(null);

    const [vaultResources, setResources] = useState<galleryResource[] | null>(null);
    const [vaultStatistics, setStats] = useState<{ limit: number, size: number } | null>(null);
    const [previewedResource, setPreviewResource] = useState<galleryResource | null>(null);
    const [showVaultUpload, setVaultUploadVis] = useState<boolean>(false);

    const copyResourceLinkRef = useRef<HTMLButtonElement>(null);

    function discard() {
        const wikiAutosaves: ArticleData[] = utils.parseStringJSON(localStorage.getItem("wiki-autosaves") || "null") || []
        const foundArticleIndex = wikiAutosaves.findIndex(article => article.title == articleData.title)

        if (foundArticleIndex !== -1) {
            wikiAutosaves.splice(foundArticleIndex, 1)
            localStorage.setItem("wiki-autosaves", JSON.stringify(wikiAutosaves))
            return true
        }

        return false
    }

    enum modalState {
        NONE = 0,
        GALLERY = 1,
        VAULT = 2
    }
    const [showModal, setModalVis] = useState<modalState>(modalState.NONE);

    async function fetchVault() {
        const response = await fetchWeb('/gallery?personal=true');

        if (response != undefined && response["msg"] == undefined) {
            setResources(response.data)
            setStats(response.stats || {})
        }
    }

    async function fetchGallery() {
        const response = await fetchWeb('/gallery');

        if (response != undefined && response["msg"] == undefined) {
            setResources(response.data)
        }
    }

    useEffect(() => {
        if (showModal == modalState.VAULT) fetchVault()
        if (showModal == modalState.GALLERY) fetchGallery()
    }, [modalState])

    function discardAutosave() {
        const discarded = discard()

        if (discarded) {
            navigate(cancelURL)
            return
        }

        setConfirmCancelModal(false)
    }

    useEffect(() => {
        const timeoutID = setTimeout(() => {
            if (articleData.content.replace(/ /g, "") == "") return;
            if (
                baseArticle.content == articleData.content &&
                baseArticle.title == articleData.title &&
                baseArticle.path == articleData.path
            ) return;

            const wikiAutosaves: ArticleData[] = utils.parseStringJSON(localStorage.getItem("wiki-autosaves") || "null") || []
            const autogeneratedTitle = `Untitled-${sessionStart.getTime().toString(36).toUpperCase()}`

            const articleToSave: ArticleData = {
                title: articleData.title.replace(/ /g, "") == "" ? autogeneratedTitle : articleData.title,
                content: articleData.content,
                path: articleData.path,
                tags: articleData.tags
            }

            const foundArticleIndex = wikiAutosaves.findIndex(article => article.title == articleToSave.title)

            // diffChars(wikiAutosaves[foundArticleIndex].content, articleToSave.content).length < 5
            if (foundArticleIndex !== -1 && wikiAutosaves[foundArticleIndex]) {
                wikiAutosaves[foundArticleIndex] = articleToSave;
                localStorage.setItem("wiki-autosaves", JSON.stringify(wikiAutosaves))
            }

            if (foundArticleIndex == -1) {
                localStorage.setItem("wiki-autosaves", JSON.stringify([
                    articleToSave,
                    ...wikiAutosaves
                ]))

                setArticleData(current => ({
                    ...current,
                    title: articleToSave.title
                }))
            }
            console.log("Generated auto-save")
        }, 1000)

        return () => {
            clearTimeout(timeoutID)
        }
    }, [articleData])

    useEffect(() => {
        if (responseMessage === null) return;

        const timeoutID = setTimeout(() => {
            setResponseMesage(null)
        }, 10000)

        return () => {
            clearTimeout(timeoutID)
        }
    }, [responseMessage])

    useEffect(() => {
        console.log("hey",baseArticle)
        // setArticleData(baseArticle)
    }, [baseArticle])

    async function postArticle(publish: boolean = false) {
        const response = await onSubmit(articleData, publish)
        if (response && response['msg'] == undefined) {
            setResponseMesage({
                path: `/${response.path}`,
                message: publish ? "Article sucessfully published to wiki, click here to redirect" : "Saved draft to wiki, click here to redirect"
            })
            discard()
            setArticleData({ content: "", path: "", title: "", tags: [] })
            return
        }

        setResponseMesage({
            path: `#`,
            message: "Something happened while uploading the article...",
            error: response['msg']
        })
    }
    const publishArticle = async () => postArticle(true);
    const saveDraft = async () => postArticle(false);


    if (showingPreview) return <>
        <LinkButton className='w-full mb-4' children="Exit preview" onClick={() => { setShowingPreview(false) }} />
        <ArticleViewBase article={{
            ...articleData,
            "updated_at": Date.now(),
            "published_at": Date.now(),
            user: info
        }} />
    </>

    return <>
        {responseMessage && <Link
            to={responseMessage.path}
            className={
                'text-white font-semibold block p-2 px-4 w-[calc(100%_+_2rem)] -m-4 mb-8 ' +
                (responseMessage.error ? 'bg-red-600' : 'bg-radiationGreen')
            }
            children={responseMessage.error ? responseMessage.error : responseMessage.message}
        />}
        {showModal ? <>
            <div
                className='fixed top-0 bottom-0 left-0 right-0 bg-outline backdrop-blur-sm z-50'
                onClick={() => setModalVis(modalState.NONE)}
            />
            <main className={
                'fixed z-[51] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ' +
                'bg-foreground rounded-lg w-full h-full max-w-2xl sm:max-h-96 p-4 '
            }>
                {(vaultResources && showVaultUpload == false) ? <>
                    <div className='flex items-center justify-between'>
                        <h2
                            className='text-xl font-semibold mb-2'
                            children={showModal == modalState.VAULT ? "My vault" : "Public gallery"}
                        />
                        <button
                            className='bg-red-600 text-white rounded-lg px-2 py-1'
                            children="Close"
                            onClick={() => setModalVis(modalState.NONE)}
                        />
                    </div>
                    <article className='flex flex-wrap h-72 gap-2'>
                        <section className='flex-grow overflow-y-auto basis-80 h-full'>
                            {showModal == modalState.VAULT && <button
                                className={
                                    'px-2 py-1 bg-primaryGradient text-white text-left w-full'
                                }
                                children="Upload new asset"
                                onClick={() => setVaultUploadVis(true)}
                            />}
                            {vaultResources.map(resource => (
                                <div
                                    key={resource.id}
                                    className={
                                        'flex justify-between px-2 py-1 border-b border-b-outline hover:bg-transparentPrimaryGradient ' +
                                        ((previewedResource && previewedResource.id == resource.id) ? 'bg-transparentPrimary' : '')
                                    }
                                    onClick={() => setPreviewResource(resource)}
                                >
                                    <span>{(resource.label == "" || resource.label == null)
                                        ? `${resource.timestamp}a${resource.id}.${resource.type.split("/")[1]}`
                                        : resource.label
                                    }</span>
                                    <span className='opacity-50 italic'>{resource.type}</span>
                                </div>
                            ))}
                        </section>
                        {previewedResource && <aside className='basis-48 flex-grow text-right'>
                            {{
                                "image": <img src={previewedResource.data} draggable={false} className='w-full max-h-56 object-contain bg-[linear-gradient(white,var(--outline))]' />,
                                "video": <VideoPlayer
                                    className=''
                                    src={previewedResource.data}
                                    simpleControls={true}
                                    style={{ width: '100%' }}
                                />,
                            }[previewedResource.type.split("/")[0] || ""]}
                            <button
                                className='block w-full rounded-lg bg-primary text-white my-2 p-1 transition-[background]'
                                onClick={() => {
                                    if (copyResourceLinkRef && copyResourceLinkRef.current) {
                                        const current = copyResourceLinkRef.current;
                                        current.innerText = "Link copied"
                                        current.animate([
                                            { backgroundColor: "#60b411" },
                                            { backgroundColor: "var(--primary)" }
                                        ], { duration: 400 })
                                        setTimeout(() => {
                                            current.innerText = "Copy link"
                                        }, 500)
                                    }
                                    navigator.clipboard.writeText(previewedResource.data)
                                }}
                                ref={copyResourceLinkRef}
                                children="Copy link"
                            />
                            <button
                                className='block w-full rounded-lg bg-background'
                                onClick={() => setPreviewResource(null)}
                                children="Cancel"
                            />
                        </aside>}
                    </article>
                    {(vaultStatistics && showModal == modalState.VAULT) && <AvailableSpaceIndicator
                        className='!absolute bottom-2 left-2 !w-[calc(100%_-_1rem)] mt-4 text-white'
                        bytes={vaultStatistics.size}
                        total={vaultStatistics.limit}
                    />}
                </> : showVaultUpload ? <>
                    <div className='flex items-center justify-between'>
                        <div>
                            <button
                                className='inline align-middle mr-2 bg-primary text-white rounded-lg px-2 py-1'
                                children="Back"
                                onClick={() => setVaultUploadVis(false)}
                            />
                            <h2 className='inline text-xl align-middle font-semibold mb-2'>File upload</h2>
                        </div>
                        <button
                            className='bg-red-600 text-white rounded-lg px-2 py-1'
                            children="Close"
                            onClick={() => setModalVis(modalState.NONE)}
                        />
                    </div>
                    <VaultFileUpload uploadCallback={(resource) => {
                        setVaultUploadVis(false)
                        setResources(current => [
                            resource.resource,
                            ...(current || [])
                        ])
                        setStats(current => ({
                            limit: (current?.limit || 0),
                            size: (current?.size || 0) + resource.size
                        }))
                        setPreviewResource(resource.resource)
                    }} />
                </> : <LoadingPage />}
            </main>
        </> : null}
        {confirmCancelModal ? <>
            <div
                className='fixed top-0 bottom-0 left-0 right-0 bg-outline backdrop-blur-sm z-50'
                onClick={() => setConfirmCancelModal(false)}
            />
            <main className={
                'fixed z-[51] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ' +
                'bg-foreground rounded-lg w-full h-full max-w-80 max-h-64 p-4 '
            }>
                <article className='flex flex-col h-full gap-2'>
                    <div className='flex-grow flex justify-center items-center'>
                        <p className='max-w-56'>You have unsaved changes. Do you want to save changes as {articleData.title}</p>
                    </div>
                    <div className='flex flex-wrap justify-between'>
                        <button
                            className='btn'
                            onClick={() => setConfirmCancelModal(false)}
                            children="Back"
                        />
                        <button
                            className='btn'
                            onClick={discardAutosave}
                            children="Discard"
                        />
                        <button
                            className='filled-btn'
                            onClick={() => navigate(cancelURL)}
                            children="Save and Close"
                        />
                    </div>
                </article>
            </main>
        </> : null}
        <div className='mb-4'>
            <img src="/images/paper_and_pencil.png" alt="paper and pencil icon" className='inline w-16 aspect-square mr-4' />
            <h2 className='inline text-3xl font-semibold mb-2 align-middle'>{title}</h2>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
            <span>Tools:</span>
            <EditorButton src='/images/folder.png' label='Vault' onClick={() => setModalVis(modalState.VAULT)} />
            <EditorButton src='/images/document.png' label='Public gallery' onClick={() => setModalVis(modalState.GALLERY)} />
        </div>
        <div className='flex flex-wrap items-center gap-1'>
            <span className='block'>Wiki link: https://wiki.angeldav.net/</span>
            <BaseInput
                value={articleData.path}
                onChange={({ target }) => setArticleData(last => ({ ...last, path: target.value }))}
                placeholder='characters/bob'
                className='flex-grow'
            />
        </div>
        <div className='flex flex-wrap items-center gap-1 mt-2'>
            <span className='block'>Title</span>
            <BaseInput
                value={articleData.title}
                onChange={({ target }) => setArticleData(last => ({ ...last, title: target.value }))}
                placeholder='example: Turret experiment aka. “BOB”'
                className='flex-grow'
            />
        </div>
        <div className='flex flex-wrap items-center gap-1 mt-2'>
            <span className='block pr-2'>Tags</span>
            <div className='flex flex-wrap flex-grow gap-1 bg-darkForeground p-1 rounded-xl min-h-11'>{articleData.tags.map((tag, index) => (
                <div key={tag.id} className='inline-block bg-darkPrimary px-4 py-1 rounded-xl text-white'>
                    <span className='align-middle mr-1'>{tag.name}</span>
                    <img src="/images/cross.png" alt="delete icon" className='inline h-6' onClick={() => setArticleData(last => {
                        const newList = [...last.tags]
                        newList.splice(index, 1)
                        return {
                            ...last,
                            tags: newList
                        }
                    })} />
                </div>
            ))}</div>
            <select id="mode" defaultValue="0" className='outline-none bg-foreground text-onBackground' onChange={({ target }) => {
                const selectedTag = utils.parseStringJSON(target.value)
                if (articleData.tags.find(tag => tag.id == selectedTag["category_id"]) == null) setArticleData(last => ({ ...last, tags: [...last.tags, { id: selectedTag["category_id"], name: selectedTag.name }] }))
                target.value = "0"
            }}>
                <option value="0">Add category</option>
                {availableTags.map(tag => {
                    if (articleData.tags.find(articleTag => articleTag.id == parseInt(tag.category_id)) == null) {
                        return <option key={tag.category_id} value={JSON.stringify(tag)}>{tag.name}</option>
                    }
                })}
            </select>
        </div>
        <span className='block mt-2'>{"Wiki body (Markdown and HTML allowed)"}</span>
        <BaseTextArea
            value={articleData.content}
            onChange={({ target }) => setArticleData(last => ({ ...last, content: target.value }))}
            className='bg-darkForeground p-2 rounded-xl outline-none my-1 overflow-x-scroll'
        />
        <div className='flex flex-wrap *:flex-grow gap-2 mt-4'>
            <LinkButton children="Cancel" selected={false} onClick={() => articleData.title.replace(/ /g, "") == "" ? navigate(cancelURL) : setConfirmCancelModal(true)} />
            <LinkButton children="Preview page" selected={false} onClick={() => { setShowingPreview(true) }} />
            {showDraftActions && <LinkButton children="Save draft" selected={false} onClick={saveDraft} />}
            <LinkButton children="Publish" onClick={publishArticle} />
        </div>
    </>
}

export default ArticleEditor